import React, { useState, useEffect, useCallback } from 'react';
import { Tenant } from '../../types/tenant';
import { Bill } from '../../types/settlement';
import { formatCurrency, formatDate } from '../../utils/formatters.ts';
import { supabase } from '../../services/supabase.ts';
import { BillStatus } from '../../types/payment';
import '../../styles/Settlement.css';
import ConfirmationModal from '../shared/ConfirmationModal.tsx';

interface SettlementModalProps {
  tenant: Tenant;
  onClose: () => void;
  onDeboard?: (settlementData: any) => Promise<void>;
  onSuccess?: () => void;
}

interface EnhancedBill extends Bill {
  pending_amount: number;  // Make it required, not optional
}

const SettlementModal: React.FC<SettlementModalProps> = ({ 
  tenant, 
  onClose, 
  onDeboard,
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [totalRentDue, setTotalRentDue] = useState(0);
  const [additionalPayment, setAdditionalPayment] = useState(0);
  const [notes, setNotes] = useState('');
  const [previousSettlements, setPreviousSettlements] = useState<any[]>([]);
  const [pendingBills, setPendingBills] = useState<EnhancedBill[]>([]);
  const [, setSettlementLoss] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const fetchAmounts = useCallback(async () => {
    try {
      const { data: bills, error: billsError } = await supabase
        .from('bills')
        .select('*, payments(total_amount)')
        .eq('tenant_id', tenant.id)
        .in('status', ['Unpaid', 'Partial'])
        .order('created_at', { ascending: true });

      if (billsError) throw billsError;

      const processedBills = (bills || []).map(bill => ({
        ...bill,
        pending_amount: bill.status === 'Partial' 
          ? bill.amount - (bill.paid_amount || 0)  // Fixed calculation for partial payments
          : bill.amount
      })) as EnhancedBill[];

      setPendingBills(processedBills);

      const totalPendingAmount = processedBills.reduce((sum, bill) => 
        sum + bill.pending_amount, 0
      );

      setTotalRentDue(totalPendingAmount);

      const { data: settlements } = await supabase
        .from('tenant_settlements')
        .select('*')
        .eq('tenant_id', tenant.id)
        .order('created_at', { ascending: false });

      // Safe check for settlements and length
      const validSettlements = settlements || [];
      if (validSettlements.length > 0) {
        setPreviousSettlements(validSettlements);
      }

    } catch (error) {
      alert('Error loading payment details');
    }
  }, [tenant.id]);
  useEffect(() => {
    fetchAmounts();
  }, [fetchAmounts]);

  const currentBalance = totalRentDue;
  const finalOutstanding = Math.max(0, currentBalance - additionalPayment);

  const handleAdditionalPaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = Math.max(0, parseFloat(e.target.value) || 0);
    setAdditionalPayment(inputValue);
  };

  const processSettlement = async (totalSettlementAmount: number) => {
    let remainingAmount = totalSettlementAmount
    let totalLoss = 0;

    // Calculate loss from pending bills
    for (const bill of pendingBills) {
      const pendingAmount = bill.pending_amount;

      if (remainingAmount >= pendingAmount) {
        remainingAmount -= pendingAmount;
      } else {
        totalLoss += pendingAmount - remainingAmount;
        remainingAmount = 0;
      }
    }

    setSettlementLoss(totalLoss);

    // Mark all pending bills as paid
    const { error: billsError } = await supabase
      .from('bills')
      .update({
        status: 'Paid' as BillStatus,
        paid_amount: (b: any) => b.amount,
        payment_date: new Date().toISOString().split('T')[0],
      })
      .in('id', pendingBills.map(b => b.id));

    if (billsError) throw billsError;

    return totalLoss;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowConfirmation(true);
  };

  const confirmSettlement = async () => {
    setLoading(true);
    try {
      const settlementAmount = additionalPayment;
      const lossAmount = await processSettlement(settlementAmount);

      if (additionalPayment > 0) {
        const { error: paymentError } = await supabase
          .from('payments')
          .insert([{
            tenant_id: tenant.id,
            property_id: tenant.property_id,
            total_amount: additionalPayment,
            payment_date: new Date().toISOString().split('T')[0],
            payment_mode: 'Cash',
            payment_to: 'Other',
            notes: `Settlement payment - ${notes}\nLoss Amount: ${formatCurrency(lossAmount)}`,
            bill_id: pendingBills[0]?.id || null, // Add reference to first pending bill
            settlement_payment: true
          }]);

        if (paymentError) throw paymentError;
      }

      const settlementData = {
        tenant_id: tenant.id,
        property_id: tenant.property_id,
        total_amount: totalRentDue,
        paid_amount: additionalPayment,
        outstanding_amount: Math.max(0, totalRentDue - additionalPayment),
        security_deposit: tenant.security_deposit,
        additional_payment: additionalPayment,
        settlement_date: new Date().toISOString().split('T')[0],
        settlement_notes: `${notes}\nTotal Loss: ${formatCurrency(lossAmount)}`,
        status: 'Cleared'
      };

      if (!onDeboard) {
        const { error: settlementError } = await supabase
          .from('tenant_settlements')
          .insert([settlementData]);

        if (settlementError) throw settlementError;

        const { error: tenantError } = await supabase
          .from('tenants')
          .update({ 
            status: 'Inactive',
            deboarding_date: new Date().toISOString().split('T')[0]
          })
          .eq('id', tenant.id);

        if (tenantError) throw tenantError;

        const { error: propertyError } = await supabase
          .from('properties')
          .update({ status: 'Vacant' })
          .eq('id', tenant.property_id);

        if (propertyError) throw propertyError;
      } else {
        await onDeboard(settlementData);
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      alert(error.message || 'Failed to process settlement');
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  return (
    <div className="modal">
      <div className="settlement-modal">
        <div className="settlement-header">
          <h2>Payment Settlement</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="settlement-content">
            <div className="settlement-main">
              {previousSettlements.length > 0 && (
                <div className="detail-row info">
                  <p className="info-text">
                    💡 Previous settlement: {formatCurrency(previousSettlements[0].outstanding_amount)} outstanding
                    from {formatDate(previousSettlements[0].settlement_date)}
                  </p>
                </div>
              )}

              <div className="bill-summary">
                <div className="bill-summary-header">
                  <span>Total Amount Due</span>
                  <span className="amount-due">{formatCurrency(totalRentDue)}</span>
                </div>
              </div>

              <div className="detail-row">
                <label>Security Deposit</label>
                <span className="value">{formatCurrency(tenant.security_deposit)}</span>
              </div>

              <div className="detail-row">
                <label>Additional Payment</label>
                <input
                  type="number"
                  className="amount-input"
                  value={additionalPayment}
                  onChange={handleAdditionalPaymentChange}
                  min="0"
                  step="any"
                />
              </div>

              <div className="detail-row">
                <label>Final Outstanding</label>
                <span className="value amount-outstanding">
                  {formatCurrency(finalOutstanding)}
                </span>
              </div>

              <div className="detail-row">
                <label>Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Add settlement notes..."
                />
              </div>

              {finalOutstanding > 0 && (
                <div className="detail-row warning">
                  <p className="warning-text">
                    ⚠️ Outstanding: {formatCurrency(finalOutstanding)}
                  </p>
                </div>
              )}
            </div>

            <div className="settlement-sidebar">
              <div className="bill-summary-header">
                <h3>Pending Bills</h3>
                <span className="bill-count">{pendingBills.length} bills</span>
              </div>
              
              <div className="bills-list">
                {pendingBills.map(bill => (
                  <div key={bill.id} className="bill-item">
                    <div className="bill-date">{formatDate(bill.month)}</div>
                    <div className="bill-details">
                      <span className="bill-amount">{formatCurrency(bill.amount)}</span>
                      <span className="bill-pending">
                        {formatCurrency(bill.pending_amount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Processing...' : 'Confirm Settlement'}
            </button>
          </div>
        </form>
      </div>

      <ConfirmationModal
        isOpen={showConfirmation}
        message={`Are you sure you want to deboard ${tenant.full_name}? This action will:
        • Mark all pending bills as paid
        • Update tenant status to inactive
        • Mark property as vacant
        • Record final settlement`}
        onConfirm={confirmSettlement}
        onCancel={() => setShowConfirmation(false)}
        confirmText="Deboard"
        cancelText="Close"
        isLoading={loading}
      />
    </div>
  );
};

export default SettlementModal;
